// src/features/quality/components/SubmitForApprovalDialog.tsx
// ADR-091 Rule QC-FE-04.
//
// Reads from the `workflow` domain's generated client — this is reading
// the SHARED generated API client (api/generated/workflow), not importing
// from `features/workflow` directly, so it does not violate ADR-072
// Rule FMS-01's no-cross-feature-import boundary.

import { useState } from "react";

// TODO(verify): confirm exact hook/module names — inferred from the same
// v1{Basename}{Action} pattern seen elsewhere (v1WorkflowRevisionsListParams.ts
// already exists in the generated client per the earlier file listing).
import { useV1WorkflowRevisionsList } from "@/api/generated/v1/v1";

export interface SubmitForApprovalDialogProps {
  onSubmit: (workflowRevisionId: string) => Promise<void>;
  isSubmitting: boolean;
  onClose: () => void;
}

export function SubmitForApprovalDialog({
  onSubmit,
  isSubmitting,
  onClose,
}: SubmitForApprovalDialogProps) {
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Rule QC-FE-04: only PUBLISHED WorkflowRevisions are valid approval
  // definitions (ADR-047 — a Draft workflow definition cannot govern a
  // real approval).
  const { data, isLoading } = useV1WorkflowRevisionsList({
    status: "published",
  });
  const publishedWorkflows = data?.results ?? [];

  async function handleConfirm() {
    setError(null);
    if (!selectedId) {
      setError("یک Workflow تأیید انتخاب کنید.");
      return;
    }
    try {
      await onSubmit(selectedId);
    } catch (err: any) {
      setError(
        err?.response?.data?.workflow_revision_id?.[0] ??
          err?.response?.data?.non_field_errors?.[0] ??
          "خطا در ارسال برای تأیید."
      );
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel w-full max-w-md p-6">
        <h3 className="text-lg font-bold mb-4">ارسال برای تأیید</h3>

        {isLoading ? (
          <p className="text-muted text-sm">در حال بارگذاری Workflowها...</p>
        ) : publishedWorkflows.length === 0 ? (
          <p className="text-sm text-muted">
            هیچ Workflow تأیید منتشرشده‌ای تعریف نشده — ابتدا یک Workflow
            برای این منظور در بخش «گردش کار» بسازید و منتشر کنید.
          </p>
        ) : (
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="input-field w-full mt-2"
          >
            <option value="">— انتخاب کنید —</option>
            {publishedWorkflows.map((wf: any) => (
              <option key={wf.id} value={wf.id}>
                {wf.family_name
                  ? `${wf.family_name} — Rev ${wf.revision_no}`
                  : wf.id}
              </option>
            ))}
          </select>
        )}

        {error && (
          <p className="text-red-500 text-sm mt-3">
            {error}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={
              isSubmitting || isLoading || publishedWorkflows.length === 0
            }
          >
            ارسال
          </button>
          <button className="btn-secondary" onClick={onClose} disabled={isSubmitting}>
            انصراف
          </button>
        </div>
      </div>
    </div>
  );
}
